import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { type ConstructionTypeData } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import type { IListItem } from 'types/Contract';
import { newDate, toISOString } from 'utils';

import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { BimActKc2OtherEditForm } from '../BimActKc2OtherEdit/BimActKc2OtherEditForm';
import { useBimActKC2Other } from '../BimActKc2OtherEdit/hooks/useBimActKC2Other';
import { useBimWorkOrderSeasons } from '../hooks/useBimWorkOrderSeasons';
import { bimActKc2OtherStatusesListItems } from '../types';

import { useBimUpdateActKC2OtherCorrection } from './hooks/useBimUpdateActKC2OtherCorrection';

interface IBimEditActKC2OtherCorrection {
    actKc2OtherId: number;
    constructionType?: ConstructionTypeData;
    selectedObject?: IListItem<number>;
    workOrderId?: number;
    onClose: () => void;
    refreshProject: () => void;
    makeRefreshCurrentTable: () => void;
}

export const BimEditActKC2OtherCorrection: React.FC<IBimEditActKC2OtherCorrection> = (props) => {
    const {
        actKc2OtherId,
        constructionType,
        selectedObject,
        workOrderId,
        onClose,
        makeRefreshCurrentTable,
        refreshProject,
    } = props;

    const { bimWorkOrderSeasonsData, bimWorkOrderSeasonsStatus } = useBimWorkOrderSeasons(workOrderId, true);

    const { bimActKC2OtherData, bimActKC2OtherStatus } = useBimActKC2Other(actKc2OtherId);

    const onSaveSuccess = useCallback(() => {
        onClose();
        refreshProject?.();
        makeRefreshCurrentTable?.();
    }, [makeRefreshCurrentTable, onClose, refreshProject]);

    const { bimUpdateActKC2OtherCorrectionStatus, bimUpdateActKC2OtherCorrectionMakeRequest } =
        useBimUpdateActKC2OtherCorrection({
            actKc2OtherId,
            onSuccess: onSaveSuccess,
        });

    const { formState, handleSubmit, control, setValue, watch, clearErrors } = useForm({
        mode: 'onChange',
        defaultValues: {
            constructionType: constructionType?.name,
            objectName: selectedObject?.label,
        } as any,
    });

    const seasonsItems = useMemo(
        () =>
            bimWorkOrderSeasonsData?.bimWorkOrderSeasons?.items?.map((item) => ({
                id: item.workOrderSeasonId,
                label: item.year,
            })) || [],
        [bimWorkOrderSeasonsData?.bimWorkOrderSeasons?.items],
    );

    const statusItems = useMemo(() => {
        const actKc2Other = bimActKC2OtherData?.bimActKC2Other?.data;
        const availableStatuses = [
            ...(actKc2Other?.statusFollowers || []),
            ...(actKc2Other?.status ? [actKc2Other?.status] : []),
        ];

        return !availableStatuses.length
            ? bimActKc2OtherStatusesListItems
            : bimActKc2OtherStatusesListItems.filter((a) => availableStatuses.includes(a.id));
    }, [bimActKC2OtherData?.bimActKC2Other?.data]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    const onSubmitDialog = useCallback(
        (formData) => {
            const date = formData.correctionDate ? toISOString(formData.correctionDate) : formData.correctionDate;

            bimUpdateActKC2OtherCorrectionMakeRequest(formData?.status?.id, date);
        },
        [bimUpdateActKC2OtherCorrectionMakeRequest],
    );

    useEffect(() => {
        if (bimActKC2OtherData?.bimActKC2Other?.data) {
            const actKc2Other = bimActKC2OtherData?.bimActKC2Other?.data;

            setValue(
                'status',
                bimActKc2OtherStatusesListItems.find((s) => s.id === actKc2Other.status),
            );

            setValue(
                'season',
                seasonsItems.find((s) => s.id === actKc2Other.workOrderSeason?.workOrderSeasonId),
            );

            setValue('number', actKc2Other.number);
            setValue('fullNumber', actKc2Other.fullNumber);
            setValue('date', newDate(actKc2Other.date));
            setValue('correctionDate', newDate(actKc2Other.correctionDate));
            setValue('reportingDate', [newDate(actKc2Other.startDate), newDate(actKc2Other.finishDate)]);
            setValue('contractor', actKc2Other.contractor);
            setValue('contractorAddress', actKc2Other.contractorAddress);
        }
    }, [bimActKC2OtherData?.bimActKC2Other?.data, seasonsItems, setValue]);

    return (
        <ModalDialog
            title="Реквизиты корректировочного Акта КС-2"
            isOpen
            isOnClickOutsideDisable
            btnConfirmLabel="Сохранить"
            style={{ width: 600 }}
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmitDialog)}
            isLoading={[bimWorkOrderSeasonsStatus, bimActKC2OtherStatus].includes(RequestStatus.FETCHING)}
            isCommit={bimUpdateActKC2OtherCorrectionStatus === RequestStatus.FETCHING}
        >
            <BimActKc2OtherEditForm
                watch={watch}
                clearErrors={clearErrors}
                control={control}
                seasonsItems={seasonsItems}
                setValue={setValue}
                statusItems={statusItems}
                isCorrection
                isEditMode
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

BimEditActKC2OtherCorrection.displayName = 'BimEditActKC2OtherCorrection';
